// Keeps three custom properties (on <html>) in sync with the actual visible viewport via
// `window.visualViewport`, for Mobile keyboard-aware popup sizing/positioning:
//   --hf-vvh              visible viewport height (px)
//   --hf-vv-top           visible viewport's offset from the top of the layout viewport (px)
//   --hf-vv-bottom-inset  how much of the bottom of the layout viewport is currently covered —
//                         effectively the on-screen keyboard's height when one is open (px)
//
// Why this is needed: on Mobile Safari/Chrome, opening the on-screen keyboard shrinks
// `visualViewport.height` live but leaves `window.innerHeight` (and therefore `100vh`/`inset:0`/
// `bottom:0`/`top:50%`-based sizing and positioning) unchanged. A popup that only capped its
// max-height off a `vh` unit still centered/anchored itself against the STALE, taller viewport,
// so it could shrink correctly yet still end up positioned (partly) behind the keyboard instead
// of shifted above it. Mobile-only CSS reads these three variables to both resize AND
// reposition itself against the real visible area; Desktop/Tablet CSS never references them, so
// setting them has no effect there.
export function initViewportHeightTracking() {
  if (typeof window === "undefined") return;
  const root = document.documentElement;
  const vv = window.visualViewport;

  const update = () => {
    const height = vv ? vv.height : window.innerHeight;
    const top = vv ? vv.offsetTop : 0;
    const bottomInset = vv
      ? Math.max(0, window.innerHeight - height - top)
      : 0;
    root.style.setProperty("--hf-vvh", `${height}px`);
    root.style.setProperty("--hf-vv-top", `${top}px`);
    root.style.setProperty("--hf-vv-bottom-inset", `${bottomInset}px`);
  };

  update();

  if (vv) {
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
  } else {
    // Older WebViews without visualViewport support — falls back to the previous static
    // behavior (window.innerHeight doesn't react to the keyboard on iOS, but at least tracks
    // real orientation/resize changes everywhere else). --hf-vv-top/--hf-vv-bottom-inset stay at
    // their 0px defaults, matching the pre-keyboard-aware behavior.
    window.addEventListener("resize", update);
  }
}
